import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import OutputText from '@ey/canvascoreservices/OutputText';

import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {communicationIcMailOutline24px, communicationIcPhone24px} from '@ey/motif-react-wrapper/assets/icons';

import {labels, currentResource} from '../../../../util/utils';
import UtilityFunctions from '../../../../util/utilityfunctions';
import selectAccessPermissions from '../../../../reducers/Project/selectors/selectAccessPermissions';

import Ellipsis from '../../../Common/Ellipsis/Ellipsis';
import ColoredChip from '../../../Common/ColoredChip';

import DashboardCheck from '../DashboardCheck';
import getAvailableEntityList from '../../../Pages/ContactDB/Toolbar/GenerateMessage/GMForm/utils/getAvailableEntityList';
import normalizeContactList from '../../../Pages/ContactDB/Toolbar/GenerateMessage/GMForm/utils/normalizeContactList';

import Modal from '../../../CoreWrappers/Modal';
import User from '../../../CoreWrappers/User';
import StyledModalBody from './StyledModalBody';

const ProfileModal = ({onCloseModal, profile}) => {
	const contactList = useSelector((store) =>
		store.contacts?.entities?.length > 0
			? normalizeContactList(getAvailableEntityList(store.contacts.entities, false))
			: []
	);
	const {groupRiskAssessmentAccess} = selectAccessPermissions();
	const entitiesAvailable = contactList.filter(({emailAddress}) => emailAddress === profile.emailAddress);
	const isDarkMode = UtilityFunctions.isDarkMode();
	const renderChip = (text, textProps = {}) =>
		text && (
			<ColoredChip>
				<OutputText {...textProps}>{text}</OutputText>
			</ColoredChip>
		);

	const renderTag = (text, color = '', textProps = {}) =>
		text && (
			<ColoredChip containerClassName="max-half" className={color?.toLowerCase()}>
				<OutputText {...textProps}>{<Ellipsis content={text} />}</OutputText>
			</ColoredChip>
		);

	const mapToDashboard = (dashboardKey) => {
		const lowercaseKey = dashboardKey?.toLowerCase()?.replaceAll(' ', '');
		const dashboard = _.find(currentResource.dashboardContactViews, (dashboard) => {
			const dashboardValue = dashboard?.value?.toLowerCase().replaceAll(' ', '');
			return lowercaseKey === dashboardValue || lowercaseKey === `ey${dashboardValue}`;
		}) || {id: Number.MAX_VALUE, value: dashboardKey};
		if (dashboardKey?.startsWith('EY ')) return {...dashboard, value: dashboardKey};

		return dashboard;
	};

	const renderDashboardTag = (dashboard) => renderTag(dashboard.key, isDarkMode ? '' : 'bg blue-transparent');

	const renderEmail = (email) =>
		email && (
			<span className="contact-info">
				<a href={`mailto:${email}`} className={'icon-text'}>
					<MotifIcon src={communicationIcMailOutline24px} />
					<OutputText color={isDarkMode ? 'yellow400' : 'blue400'} size="L">
						{email}
					</OutputText>
				</a>
			</span>
		);

	const renderPhone = (phone) =>
		phone && (
			<span className="contact-info phone icon-text">
				<MotifIcon src={communicationIcPhone24px} />
				<OutputText size="L">{phone}</OutputText>
			</span>
		);

	const renderPersonalData = () => {
		const {isInternal, name, lastName, emailAddress, location, phone, serviceLine, roleName} = profile;
		const props = {
			firstName: name,
			lastName,
			email: emailAddress,
			isInternal,
			showInfo: false,
			showAside: false,
			size: 'xl'
		};
		return (
			<section className="modal-section contact-data">
				<User {...props} />
				<OutputText size="XXL">
					{name} {lastName}
				</OutputText>
				{isInternal && location && (
					<OutputText size="L" color="grey400">
						{location}
					</OutputText>
				)}
				<div className="flex-1">
					{renderEmail(profile.emailAddress)}
					{isInternal && renderPhone(phone)}
				</div>
				<section className="chips">
					<div className="chip-group">
						{(isInternal && [renderChip(serviceLine), renderChip(roleName), renderChip(labels.internal)]) ||
							renderChip(labels.external)}
					</div>
				</section>
			</section>
		);
	};

	const renderPropValue = (value, valueType) => {
		switch (valueType) {
			case 'boolean':
				return <DashboardCheck condition={value} />;
			default:
				return (
					<OutputText className="prop-value" size="L" weight="light">
						<Ellipsis content={value} />
					</OutputText>
				);
		}
	};

	const renderProp = (label, value, valueType = 'text') => {
		const title = `${label} `;
		return (
			<div className="flex-1-inline-horizontal prop-name">
				<OutputText size="L" weight="light" className="flex-1">
					<Ellipsis content={title} />
				</OutputText>
				{renderPropValue(value, valueType)}
			</div>
		);
	};

	const renderAccess = () => {
		const {dashboard, engagements, contactList, groupRiskAssessment} = profile;
		const hasDashboards = dashboard?.length;
		const entitiesCount = entitiesAvailable[0]?.entities?.length || 0;
		const entitiesLabel = entitiesCount === 1 ? labels.entityLabel : labels.entitiesLabel;
		const engagementsLabel = engagements.length === 1 ? labels.engagementLabel : labels.engagementsLabel;

		return (
			<span className="modal-section left">
				<OutputText className="section-title" size="XL">
					{labels.profileModalAccessSection}
				</OutputText>
				<div className="prop-group group-access">
					{renderProp(entitiesLabel, entitiesCount)}
					{renderProp(engagementsLabel, engagements.length)}
					{renderProp(labels.dashboard, hasDashboards, 'boolean')}
					{groupRiskAssessmentAccess &&
						renderProp(labels.groupRiskAssessment, groupRiskAssessment?.hasAccess, 'boolean')}
					{renderProp(labels.contactList, contactList.hasAccess, 'boolean')}
				</div>
			</span>
		);
	};

	const renderCustomAttributes = () => {
		const {customColumns, tags} = profile;
		const hasCustomAttributes = customColumns?.length || tags?.length;
		return (
			(hasCustomAttributes && (
				<span className="modal-section right section-custom-attributes">
					<OutputText className="section-title" size="XXL">
						{labels.profileModalCustomAttributesSection}
					</OutputText>
					{(tags.length && (
						<div className="chip-group">
							{_.sortBy(tags, ['name']).map(({name, color}) => {
								return renderTag(name, color);
							})}
						</div>
					)) ||
						''}
					<div className="prop-group customColumns">
						{_.sortBy(customColumns ?? [], 'columnName').map((c) =>
							renderProp(c.name, c?.options[0]?.option, c.columnType)
						)}
					</div>
				</span>
			)) ||
			''
		);
	};

	const renderDashboards = () => {
		const {dashboard, customColumns, tags} = profile;
		const hasDashboards = dashboard?.length;
		const hasCustomAttributes = customColumns?.length || tags?.length;
		return (
			(hasDashboards && (
				<span className={`modal-section ${hasCustomAttributes ? 'left' : 'right'}`}>
					<OutputText className="section-title" size="XL">
						{labels.profileModalDashboardSection}
					</OutputText>
					<div className="chip-group">
						{_.sortBy(dashboard.map(mapToDashboard), ['id', 'value']).map(renderDashboardTag)}
					</div>
				</span>
			)) ||
			''
		);
	};

	const props = {
		show: true,
		onHide: onCloseModal,
		modalsize: 'small',
		title: labels.profileModalTitle,
		closeTitle: labels.closeLabel,
		showFooter: false
	};

	return (
		<Modal {...props}>
			<StyledModalBody>
				{renderPersonalData()}
				{renderAccess()}
				{renderCustomAttributes()}
				{renderDashboards()}
			</StyledModalBody>
		</Modal>
	);
};

export default ProfileModal;

ProfileModal.defaultProps = {};

ProfileModal.propTypes = {
	onCloseModal: PropTypes.func.isRequired,
	profile: PropTypes.any
};
